import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, CheckCircle, XCircle, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { vocabularyAPI, progressAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const FillInBlank = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadVocabulary();
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll();
      const vocabularyWords = response.words || [];
      setWords(vocabularyWords);
      generateQuestions(vocabularyWords);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate fill-in-the-blank questions
  const generateQuestions = (vocabularyWords = words) => {
    if (vocabularyWords.length === 0) return;
    
    // Shuffle words first for variety, then filter and select
    const shuffledWords = [...vocabularyWords].sort(() => Math.random() - 0.5);
    const questionSet = shuffledWords
      .filter(word => word.exampleSentence)
      .slice(0, 8)
      .map((word, index) => {
        // Create a blank by replacing the Algerian word in the example sentence
        const sentence = word.exampleSentence;
        const blankSentence = sentence.replace(word.algerianWord, '______');
        
        return {
          id: index + 1,
          sentence: blankSentence,
          originalSentence: sentence,
          correctAnswer: word.algerianWord.toLowerCase(),
          word: word,
          hint: word.englishTranslation
        };
      });
    
    setQuestions(questionSet);
  };

  // Normalize Algerian Arabic text for flexible matching
  const normalizeAlgerianText = (text) => {
    return text
      .toLowerCase()
      .trim()
      // Remove common vowels that are often omitted
      .replace(/[aeiou]/g, '')
      // Normalize common letter variations
      .replace(/kh/g, 'x')  // kh and x are often used interchangeably
      .replace(/gh/g, 'r')  // gh variations
      // Remove extra spaces and punctuation
      .replace(/[.,!?;:\s]/g, '');
  };

  // Check if two Algerian words match with spelling flexibility
  const isAlgerianMatch = (userInput, correctAnswer) => {
    const normalizedUser = normalizeAlgerianText(userInput);
    const normalizedCorrect = normalizeAlgerianText(correctAnswer);
    
    // Exact match after normalization
    if (normalizedUser === normalizedCorrect) {
      return true;
    }
    
    // Allow for common vowel insertions/deletions
    // Check if one is a subset of the other (accounting for extra vowels)
    const userChars = normalizedUser.split('');
    const correctChars = normalizedCorrect.split('');
    
    // Check if user input matches the pattern with vowels removed/added
    let userIndex = 0;
    let correctIndex = 0;
    
    while (userIndex < userChars.length && correctIndex < correctChars.length) {
      if (userChars[userIndex] === correctChars[correctIndex]) {
        userIndex++;
        correctIndex++;
      } else {
        // Skip ahead in the longer string
        if (normalizedUser.length > normalizedCorrect.length) {
          userIndex++;
        } else {
          correctIndex++;
        }
      }
    }
    
    // Consider it a match if we've matched most characters
    const matchedChars = Math.min(userIndex, correctIndex);
    const minLength = Math.min(normalizedUser.length, normalizedCorrect.length);
    const matchRatio = matchedChars / Math.max(normalizedUser.length, normalizedCorrect.length);
    
    // Allow match if 80% or more characters align and minimum length is met
    return matchRatio >= 0.8 && minLength >= 3;
  };

  const checkAnswer = () => {
    if (!userAnswer.trim()) {
      toast({
        title: "Please enter an answer",
        description: "Type your answer in the input field",
        variant: "destructive"
      });
      return;
    }

    setShowResult(true);
    const isCorrect = isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord);
    
    if (isCorrect) {
      const points = hintUsed ? 0.5 : 1;
      setScore(score + points);
      toast({
        title: "Correct!",
        description: hintUsed ? "Half point earned (hint used)" : "Full point earned!",
      });
    } else {
      toast({
        title: "Not quite right",
        description: `The correct answer is "${questions[currentQuestion].correctAnswer}"`,
        variant: "destructive"
      });
    }
  };

  const nextQuestion = async () => {
    // Update progress for current question
    const currentQ = questions[currentQuestion];
    const normalizeStr = (str) => str.toLowerCase().trim().replace(/[\s-]/g, '');
    const isCorrect = normalizeStr(userAnswer) === normalizeStr(currentQ.correctAnswer);
    const finalScore = hintUsed ? (isCorrect ? 0.5 : 0) : (isCorrect ? 1 : 0);
    
    if (currentQ && currentQ.word) {
      try {
        await progressAPI.update({
          category: currentQ.word.category,
          wordId: currentQ.word.id,
          gameType: "fill-in-blank",
          score: finalScore,
          completed: true
        });
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    }
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswer('');
      setShowResult(false);
      setHintUsed(false);
    } else {
      setGameComplete(true);
      toast({
        title: "Game Complete!",
        description: `Final Score: ${score.toFixed(1)}/${questions.length}`,
      });
    }
  };

  const useHint = () => {
    setHintUsed(true);
    toast({
      title: "Hint",
      description: `This word means: ${questions[currentQuestion].hint}`,
    });
  };

  const resetGame = () => {
    setCurrentQuestion(0);
    setScore(0);
    setUserAnswer('');
    setShowResult(false);
    setGameComplete(false);
    setHintUsed(false);
    generateQuestions(words);
  };

  const getScoreColor = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Game...</h1>
        <p className="text-gray-600 mb-4">No sentences with examples found in vocabulary.</p>
        <Link to="/vocabulary">
          <Button>Add vocabulary with example sentences</Button>
        </Link>
      </div>
    );
  }

  if (gameComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <Card className="max-w-md mx-auto shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Game Complete!</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-4xl font-bold mb-4 ${getScoreColor()}`}>
                {score.toFixed(1)}/{questions.length}
              </div>
              <div className={`text-2xl font-semibold mb-6 ${getScoreColor()}`}>
                {percentage}%
              </div>
              
              <div className="space-y-4">
                <div className="text-lg">
                  {percentage >= 80 ? "Excellent work! 🎉" : 
                   percentage >= 60 ? "Good job! Keep practicing 👍" : 
                   "Keep studying and try again! 📚"}
                </div>
                
                <div className="flex space-x-4">
                  <Button onClick={resetGame} className="flex-1">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Play Again
                  </Button>
                  <Link to="/games" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Games
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/games">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Games
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Fill in the Blanks</h1>
        <Button variant="outline" size="sm" onClick={resetGame}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Progress and Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm text-gray-600">
            Score: {score.toFixed(1)}/{questions.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question */}
      <Card className="mb-8 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg">Fill in the blank with the correct Algerian word:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xl text-center mb-6 p-4 bg-gray-50 rounded-lg font-medium leading-relaxed">
            {currentQ.sentence}
          </div>
          
          <div className="space-y-4">
            <div className="flex space-x-2">
              <Input
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                placeholder="Type your answer here..."
                className="text-lg"
                disabled={showResult}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !showResult) {
                    checkAnswer();
                  }
                }}
              />
              {!showResult && (
                <Button onClick={checkAnswer} disabled={!userAnswer.trim()}>
                  Check
                </Button>
              )}
            </div>

            {!showResult && !hintUsed && (
              <Button variant="outline" onClick={useHint} className="w-full">
                <Lightbulb className="w-4 h-4 mr-2" />
                Use Hint (Half Points)
              </Button>
            )}

            {showResult && (
              <div className={`p-4 rounded-lg ${
                isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord)
                  ? 'bg-green-100 border border-green-300'
                  : 'bg-red-100 border border-red-300'
              }`}>
                <div className="flex items-center space-x-2 mb-2">
                  {isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord) ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${
                    isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord)
                      ? 'text-green-800'
                      : 'text-red-800'
                  }`}>
                    {isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord)
                      ? (hintUsed ? 'Correct! (Half Points)' : 'Correct!')
                      : 'Incorrect'
                    }
                  </span>
                </div>
                
                <div className={isAlgerianMatch(userAnswer, questions[currentQuestion].word.algerianWord) ? 'text-green-700' : 'text-red-700'}>
                  <p className="mb-2">
                    <strong>Correct answer:</strong> {currentQ.word.algerianWord}
                  </p>
                  <p className="mb-2">
                    <strong>English meaning:</strong> {currentQ.word.englishTranslation}
                  </p>
                  <p className="italic">
                    <strong>Complete sentence:</strong> &ldquo;{currentQ.originalSentence}&rdquo;
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Debug info removed - was showing answer to users */}

      {/* Next Button */}
      {showResult && (
        <div className="text-center">
          <Button onClick={nextQuestion} size="lg">
            {currentQuestion < questions.length - 1 ? 'Next Question' : 'View Results'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FillInBlank;